/**
 * How far the on-screen keyboard covers the layout viewport.
 *
 * iOS does not shrink `100dvh` when the keyboard opens, so a composer pinned
 * to the bottom of the page slides under the keys. The visual viewport does
 * shrink. A few pixels of browser chrome are not a keyboard; only a cover
 * large enough to be one is reported, so the URL bar collapsing does not
 * jump the page.
 */
export function keyboardInsetPx(layoutHeight: number, visualHeight: number, offsetTop: number): number {
  if (!Number.isFinite(layoutHeight) || !Number.isFinite(visualHeight) || !Number.isFinite(offsetTop)) return 0
  const covered = layoutHeight - visualHeight - offsetTop
  if (covered < 80) return 0
  return Math.round(covered)
}

/** Keeps `--keyboard-inset` in step with the visual viewport. */
export function bindKeyboardInset(target: HTMLElement = document.documentElement): () => void {
  const viewport = window.visualViewport
  if (viewport === null || viewport === undefined) return () => undefined

  const apply = (): void => {
    const inset = keyboardInsetPx(window.innerHeight, viewport.height, viewport.offsetTop)
    target.style.setProperty('--keyboard-inset', `${inset}px`)
  }

  apply()
  viewport.addEventListener('resize', apply)
  viewport.addEventListener('scroll', apply)
  window.addEventListener('resize', apply)
  return () => {
    viewport.removeEventListener('resize', apply)
    viewport.removeEventListener('scroll', apply)
    window.removeEventListener('resize', apply)
    target.style.removeProperty('--keyboard-inset')
  }
}
